import { XStack, YStack } from "tamagui";

import {
  type BillingIntervalModel,
  GrantType,
  type PricingModel,
  ProductOption,
  type SubscriptionProductOption,
  type VisitOptionModel,
} from "@/api/generated/models";
import Doctor from "@/assets/images/doctor.png";
import {
  BulletedList,
  Button,
  Card,
  Heading,
  Image,
  Paragraph,
  Text,
} from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { usePatientResponsibility } from "@/conversation/sidebar/Payment/usePatientResponsibility";
import {
  type OfferProductOption,
  STATIC_OPTIONS_BY_VISIT_TYPE,
} from "@/conversation/sidebar/Payment/visitOptions";
import { useAuthState } from "@/hooks/useAuthState";
import { useLocalPartnerName } from "@/hooks/useLocalPartnerName";
import { formatCentsAsPriceLabel } from "@/utils/formatting";

type PricedVisitOption = {
  priceCents: number;
  type: PricingModel["type"];
};

const MEDICAL_GROUP_TAGLINE =
  "Curai Health Medical Group is the leading virtual care clinic in the US.";

const COVERED_VISIT_BULLETS = [
  "No additional charge",
  "Chat, video, or message",
  "Available 24/7 nationwide",
];

const DEFAULT_BULLETS = [
  "Available 24/7 nationwide",
  "4.8★ average review",
  "1M patients seen since 2019",
];

type OfferVariation =
  | "subscription_followup"
  | "sponsored_followup"
  | "followup"
  | "subscription"
  | "sponsored"
  | "covered"
  | "async_rx"
  | "cash_pay";

type OfferContext = {
  partnerName?: string | null;
  pricedOptions?: PricedVisitOption[];
};

type OfferCopy = {
  badge: string;
  title: string;
  description: string;
  bullets: string[];
};

const COPY_BY_OFFER: {
  [variation in OfferVariation]: (ctx: OfferContext) => OfferCopy;
} = {
  subscription_followup: () => ({
    badge: "Included in plan",
    title: "Your follow-up is covered — no extra charge",
    description:
      "Continue care with your doctor. Follow-ups are included in your plan",
    bullets: [
      "Pick up where you left off",
      "Chat, video, or message",
      "Available 24/7 nationwide",
    ],
  }),
  sponsored_followup: ({ partnerName }: OfferContext) => ({
    badge: "Included",
    title: `Your follow-up is covered${partnerName ? ` by ${partnerName}` : ""}`,
    description:
      "Continue your care — your follow-up is included as part of your original visit.",
    bullets: COVERED_VISIT_BULLETS,
  }),
  followup: () => ({
    badge: "Included in visit",
    title: "Your follow-up is part of your original visit",
    description:
      "Continue your care at no additional cost. Follow-ups are included with your visit.",
    bullets: COVERED_VISIT_BULLETS,
  }),
  subscription: () => ({
    badge: "Included in plan",
    title: "Start a new visit anytime with your Curai plan",
    description:
      "Your plan includes unlimited visits. See a doctor about anything, anytime.",
    bullets: [
      "Unlimited visits, no extra cost",
      "Chat, video, or message",
      "4.8★ average review",
    ],
  }),
  sponsored: ({ partnerName }: OfferContext) => ({
    badge: "Included",
    title: `Your visit is covered${partnerName ? ` by ${partnerName}` : ""}`,
    description:
      "Curai Health Medical Group is the leading virtual care clinic in the US.",
    bullets: DEFAULT_BULLETS,
  }),
  // Used e.g. when a T1 Async Rx is rejected and the clinician requires the user to come in
  // for a live visit. We purposely don't name the visit type modality and stay generic.
  covered: () => ({
    badge: "Included",
    title: "Your visit is covered",
    description: "Start your visit with us at no additional cost.",
    bullets: ["No additional charge", "Available 24/7 nationwide"],
  }),
  cash_pay: ({ pricedOptions = [] }: OfferContext) => ({
    badge:
      pricedOptions.length > 0
        ? `Starting at ${formatCentsAsPriceLabel(pricedOptions[0].priceCents)}`
        : "",
    title: "Get seen today — no insurance needed",
    description:
      "Curai Health Medical Group is the leading virtual care clinic in the US.",
    bullets: DEFAULT_BULLETS,
  }),
  async_rx: ({ pricedOptions = [] }: OfferContext) => ({
    badge:
      pricedOptions.length > 0
        ? formatCentsAsPriceLabel(pricedOptions[0].priceCents)
        : "",
    title: "Send request for a prescription",
    description:
      "After this request is sent, a licensed clinician will review your information and decide whether to approve the prescription or recommend next steps. You can expect to get a response from one of our clinicians in 30-60 minutes.",
    bullets: [],
  }),
};

export const getOfferCopyText = () =>
  `${COPY_BY_OFFER["cash_pay"]({}).title}\n\n${MEDICAL_GROUP_TAGLINE}`;

type OfferCardProps = {
  onStartVisit: (chosenProductOption?: ProductOption) => void | Promise<void>;
  offerVariation: OfferVariation;
  partnerName?: string | null;
  productOptions?: OfferProductOption[];
};

// e.g. { interval: "month", interval_count: 1 } -> "/mo"; { "month", 3 } -> "/3 mo".
const billingIntervalSuffix = ({
  interval,
  interval_count,
}: BillingIntervalModel): string => {
  const unit = { day: "day", week: "wk", month: "mo", year: "yr" }[interval];
  return interval_count === 1 ? `/${unit}` : `/${interval_count} ${unit}`;
};

type OfferButton = {
  label: string;
  productOption?: ProductOption;
  dataTrackingId?: string;
};

const getSubscriptionOfferButton = (
  productOptions: OfferProductOption[],
): OfferButton | null => {
  const subscriptionOption = productOptions.find(
    (option): option is SubscriptionProductOption =>
      option.type === "subscription",
  );
  const subscriptionPricing = subscriptionOption?.pricing ?? [];
  if (subscriptionOption && subscriptionPricing.length > 0) {
    const cheapestSub = subscriptionPricing.reduce((acc, curr) =>
      curr.amount < acc.amount ? curr : acc,
    );
    return {
      label: `Unlimited visits for ${formatCentsAsPriceLabel(
        cheapestSub.amount,
      )}${billingIntervalSuffix(subscriptionOption.billing_interval)}`,
      productOption: subscriptionOption.code,
      dataTrackingId: "curai-clinic-offer-start-subscription",
    };
  }
  return null;
};

const getOfferButtons = (
  offerVariation: OfferVariation,
  productOptions: OfferProductOption[],
): OfferButton[] => {
  const maybeUpsellBtns: OfferButton[] = [];
  // Product upsells (subscription + priced one-time) only apply in the self-pay case; covered /
  // entitled offers fall through to the default "Start visit" button.
  const isSelfPay =
    offerVariation === "cash_pay" || offerVariation === "async_rx";

  if (isSelfPay) {
    const subscriptionButton = getSubscriptionOfferButton(productOptions);
    if (subscriptionButton) maybeUpsellBtns.push(subscriptionButton);
  }

  const nonSubscriptionOptions = productOptions.filter(
    (option) => option.type !== "subscription",
  );
  const cheapestNonSub = nonSubscriptionOptions
    .flatMap((option) =>
      (option.pricing ?? []).map((price) => ({
        amount: price.amount,
        productOption: option.code,
      })),
    )
    .sort((a, b) => a.amount - b.amount)[0];
  const modalities = Array.from(
    new Set(
      nonSubscriptionOptions.flatMap((option) => option.visit_types ?? []),
    ),
  );
  if (isSelfPay && nonSubscriptionOptions.length > 0 && cheapestNonSub) {
    const cheapestPriceLabel = formatCentsAsPriceLabel(cheapestNonSub.amount);
    const singleModality =
      modalities.length === 1
        ? STATIC_OPTIONS_BY_VISIT_TYPE[modalities[0]]
        : undefined;
    // async_rx is a request to a clinician, not a "visit" — label it accordingly.
    const oneTimeLabel =
      offerVariation === "async_rx"
        ? `Send to clinician for ${cheapestPriceLabel}`
        : singleModality
          ? `One time ${singleModality.title.toLowerCase()} visit for ${cheapestPriceLabel}`
          : `One time visits starting at ${cheapestPriceLabel}`;

    maybeUpsellBtns.push({
      label: oneTimeLabel,
      productOption:
        modalities.length === 1 ? cheapestNonSub.productOption : undefined,
      dataTrackingId: "curai-clinic-offer-start-one-time",
    });
  }

  return maybeUpsellBtns.length > 0
    ? maybeUpsellBtns
    : // If no upsell buttons, show default "Start visit" button
      [
        {
          label: "Start visit",
          dataTrackingId: "curai-clinic-offer-start-visit",
        },
      ];
};

const getEntitlements = (visitOptions: VisitOptionModel[]) => {
  const grantTypes = new Set<GrantType>();
  visitOptions.forEach((option) => {
    if (!option.entitlement_state?.entitled) return;

    option.entitlement_state.grant_types?.forEach((grantType) => {
      grantTypes.add(grantType);
    });
  });

  return grantTypes;
};

/** Cash-pay prices across the one-time products, ascending — for the self-pay "from $X" badge. */
const getPricedProductOptions = (
  productOptions: OfferProductOption[],
): PricedVisitOption[] =>
  productOptions
    .filter((product) => product.type !== "subscription")
    .flatMap((product) =>
      (product.pricing ?? []).map((price) => ({
        priceCents: price.amount,
        type: price.type,
      })),
    )
    .sort((a, b) => a.priceCents - b.priceCents);

const getCheapestPriceCents = (productOptions: OfferProductOption[]): number =>
  productOptions
    .flatMap((product) => (product.pricing ?? []).map((price) => price.amount))
    .sort((a, b) => a - b)[0];

export const getOfferVariation = ({
  visitOptions,
  partnerName,
}: {
  visitOptions: VisitOptionModel[];
  partnerName?: string | null;
}): OfferVariation => {
  const entitlements = getEntitlements(visitOptions);
  const hasFollowup = entitlements.has(GrantType.LIVE_VISIT_FOLLOW_UP);
  const hasPartnerAffiliation = entitlements.has(GrantType.PARTNER_AFFILIATION);
  // Both the all-you-can-eat access grant and a true subscription grant read as a subscription.
  const hasSubscription =
    entitlements.has(GrantType.STANDARD_VISIT_ACCESS) ||
    entitlements.has(GrantType.STANDARD_SUBSCRIPTION);

  if (hasFollowup) {
    if (hasSubscription) return "subscription_followup";
    if (hasPartnerAffiliation) return "sponsored_followup";
    return "followup";
  }
  if (hasSubscription) return "subscription";
  if (hasPartnerAffiliation || partnerName) return "sponsored";
  // Grant types explain why access exists, but the backend's per-modality entitlement state is
  // authoritative. This also covers consumable grants such as async-rejection recourse and
  // one-time visit credits without requiring the offer card to enumerate every grant type.
  if (visitOptions.some((option) => option.entitlement_state?.entitled)) {
    return "covered";
  }

  // When async_rx is standalone, it gets its own offer variation. If it ever shows up with any other modality then
  // we will fall back to the regular cash_pay offercard
  const isAsyncRxOnly =
    visitOptions.length > 0 &&
    visitOptions.every((option) => option.visit_type === "async_rx");
  return isAsyncRxOnly ? "async_rx" : "cash_pay";
};

export const useOfferVariation = (visitOptions: VisitOptionModel[]) => {
  const { isSignedIn, isLoading, isInitializing } = useAuthState();
  const localPartnerName = useLocalPartnerName();
  // We only surface optimistic pricing to anonymous users: on
  // sign-in the backend returns their affiliation + entitlement via
  // visitOptions.sponsorship. If a signed-in user has no backend sponsorship we
  // intentionally ignore any stale local partner intent — their affiliation is
  // wrong/expired.
  const isAnonymousResolved = !isLoading && !isInitializing && !isSignedIn;
  const partnerName =
    visitOptions.find((option) => option.sponsorship?.name)?.sponsorship
      ?.name ?? (isAnonymousResolved ? localPartnerName : null);

  return {
    offerVariation: getOfferVariation({ visitOptions, partnerName }),
    partnerName,
  };
};

const getInsuranceCoveredCopy = (
  patientResponsibilityCents: number,
): Pick<OfferCopy, "badge" | "title"> => {
  return {
    badge: formatCentsAsPriceLabel(patientResponsibilityCents),
    title:
      patientResponsibilityCents === 0
        ? "Your visit is fully covered by insurance"
        : "We accept your insurance",
  };
};

export default function OfferCard({
  offerVariation,
  partnerName,
  productOptions = [],
  onStartVisit,
}: OfferCardProps) {
  const { value: insurancePayEnabled } = useFeatureFlag("insurance-pay", false);
  const { patientResponsibilityCents } = usePatientResponsibility({
    enabled: insurancePayEnabled && offerVariation === "cash_pay",
  });

  const pricedOptions = getPricedProductOptions(productOptions);
  const baseCopy = COPY_BY_OFFER[offerVariation]({
    pricedOptions,
    partnerName,
  });

  const showInsuranceCost =
    patientResponsibilityCents !== null &&
    patientResponsibilityCents < getCheapestPriceCents(productOptions);
  let badge;
  let title;
  let offerButtons;
  if (showInsuranceCost) {
    const insuranceCopy = getInsuranceCoveredCopy(patientResponsibilityCents);
    badge = insuranceCopy.badge;
    title = insuranceCopy.title;
    offerButtons = [
      {
        label: "Start visit",
        dataTrackingId: "curai-clinic-offer-start-visit",
      },
      getSubscriptionOfferButton(productOptions),
    ].filter((v) => !!v);
  } else {
    badge = baseCopy.badge;
    title = baseCopy.title;
    offerButtons = getOfferButtons(offerVariation, productOptions);
  }

  const { description, bullets } = baseCopy;

  return (
    <Card
      display="flex"
      flexDirection="row"
      borderRadius="$200"
      overflow="hidden"
      $maxLg={{ flexDirection: "column" }}
    >
      <XStack
        width="50%"
        minWidth={0}
        backgroundColor="$fillClinician"
        $maxLg={{ minHeight: "$2000", width: "100%" }}
      >
        <YStack
          flex={1}
          gap="$150"
          paddingHorizontal="$200"
          paddingVertical="$200"
          paddingRight="$1400"
        >
          {Boolean(badge) && (
            <Text
              backgroundColor="$backgroundGround"
              paddingHorizontal="$150"
              paddingVertical="$75"
              borderRadius="$1000"
              size="$xs"
              bold
              alignSelf="flex-start"
            >
              {badge?.toUpperCase()}
            </Text>
          )}
          <Heading size="$lg" maxWidth="$2400">
            {title}
          </Heading>
        </YStack>

        <Image
          source={Doctor}
          position="absolute"
          top={0}
          bottom={0}
          right={0}
          width="$2000"
          $lg={{ right: "$300" }}
          objectFit="cover"
          objectPosition="top center"
          pointerEvents="none"
        />
      </XStack>

      <YStack
        width="50%"
        gap="$150"
        padding="$200"
        justifyContent="center"
        backgroundColor="$fillCallout"
        $maxLg={{ width: "100%" }}
      >
        <Paragraph>{description}</Paragraph>
        {Boolean(bullets.length) && (
          <BulletedList
            items={bullets}
            icon={<BulletedList.Bullet width="$50" />}
            iconContainerProps={{ width: "$100" }}
            color="$textSecondary"
            size="$sm"
            rootProps={{ gap: 0 }}
          />
        )}
        {offerButtons.map((button, idx) => (
          <Button
            key={button.dataTrackingId ?? idx}
            mode={idx === 0 ? "primary" : "secondary"}
            onPress={() => onStartVisit(button.productOption)}
            {...(button.dataTrackingId && {
              dataTrackingId: button.dataTrackingId,
            })}
          >
            {button.label}
          </Button>
        ))}
      </YStack>
    </Card>
  );
}
